/* eslint-disable max-lines */
/* eslint-disable max-len */
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useDebounce } from "../../hooks/useDebounce";
import {
  IEsimPackage,
  ILocationZone
} from "../../interfaces/esim/EsimPackages.types";
import {
  getLocationZones,
  getPackageById,
  getPackagesByLocationZone
} from "../../services/Esim.service";
import InputWrapper from "../ui/input/InputWrapper";

interface EsimPackageSelectorProps {
  value: number | null | undefined;
  onChange: (packageId: number, pkg: IEsimPackage) => void;
  error?: string;
}

const formatGB = (gb: number, isUnlimited: boolean) => {
  if (isUnlimited) return "Unlimited";
  return gb >= 1 ? `${gb} GB` : `${Math.round(gb * 1024)} MB`;
};

const EsimPackageSelector: React.FC<EsimPackageSelectorProps> = ({
  value,
  onChange,
  error
}) => {
  const [zoneSearch, setZoneSearch] = useState("");
  const [locationZones, setLocationZones] = useState<ILocationZone[]>([]);
  const [selectedZone, setSelectedZone] = useState<ILocationZone | null>(null);
  const [packages, setPackages] = useState<IEsimPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<IEsimPackage | null>(
    null
  );
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [isZoneDropdownOpen, setIsZoneDropdownOpen] = useState(false);
  const debouncedSearch = useDebounce(zoneSearch, 300);
  const initializedRef = useRef(false);

  // On mount: if we have a packageTemplateId, resolve it to zone + package
  useEffect(() => {
    if (!value || initializedRef.current) return;
    initializedRef.current = true;

    const resolve = async () => {
      setInitializing(true);
      try {
        // 1. Fetch the package to get its zone
        const pkgRes = await getPackageById(value);
        const pkg = pkgRes.data;
        if (!pkg) return;

        // 2. Find the zone from zones list
        const zonesRes = await getLocationZones(
          "",
          1,
          100
        );
        if (zonesRes.data.succeeded) {
          setLocationZones(zonesRes.data.data);
          const zone = zonesRes.data.data.find(
            (z: ILocationZone) => z.slug === pkg.locationZoneSlug
          );
          if (zone) {
            setSelectedZone(zone);
          }
        }

        // 3. Load sibling packages and pre-select
        const siblingsRes = await getPackagesByLocationZone(
          pkg.locationZoneSlug
        );
        if (siblingsRes.data) {
          setPackages(siblingsRes.data);
          const match = siblingsRes.data.find(
            (p: IEsimPackage) => p.id === value
          );
          if (match) {
            setSelectedPackage(match);
          }
        }
      } catch {
        // Silently fail — user can still pick manually
      } finally {
        setInitializing(false);
      }
    };

    void resolve();
  }, [value]);

  // Fetch location zones on search (skip if already loaded during init)
  useEffect(() => {
    if (initializing) return;
    const fetchZones = async () => {
      setLoadingZones(true);
      try {
        const res = await getLocationZones(debouncedSearch);
        if (res.data.succeeded) {
          setLocationZones(res.data.data);
        }
      } catch {
        toast.error("Gabim gjate ngarkimit te zonave.");
      } finally {
        setLoadingZones(false);
      }
    };
    void fetchZones();
  }, [debouncedSearch]);

  // Fetch packages when a zone is selected manually
  useEffect(() => {
    if (!selectedZone || initializing) return;
    // Skip if packages already loaded (from init)
    if (packages.length > 0 && selectedPackage) return;

    const fetchPackages = async () => {
      setLoadingPackages(true);
      try {
        const res = await getPackagesByLocationZone(selectedZone.slug);
        if (res.data) {
          setPackages(res.data);
        }
      } catch {
        toast.error("Gabim gjate ngarkimit te paketave.");
      } finally {
        setLoadingPackages(false);
      }
    };
    void fetchPackages();
  }, [selectedZone]);

  const handleZoneSelect = (zone: ILocationZone) => {
    setSelectedZone(zone);
    setZoneSearch("");
    setIsZoneDropdownOpen(false);
    setSelectedPackage(null);
    setPackages([]);
  };

  const handleZoneClear = () => {
    setSelectedZone(null);
    setSelectedPackage(null);
    setPackages([]);
    setZoneSearch("");
  };

  const handlePackageSelect = (pkg: IEsimPackage) => {
    setSelectedPackage(pkg);
    onChange(pkg.id, pkg);
  };

  if (initializing) {
    return (
      <div className="mb-6">
        <InputWrapper label="Paketa eSIM" error={undefined}>
          <div className="p-3 text-sm text-gray-500 ring-1 ring-blue-stroke rounded-lg">
            Duke ngarkuar paketen...
          </div>
        </InputWrapper>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Zone selector */}
      <InputWrapper label="Zona e lokacionit" error={undefined}>
        <div className="relative">
          <input
            type="text"
            value={selectedZone ? selectedZone.name : zoneSearch}
            onChange={(e) => {
              if (selectedZone) handleZoneClear();
              setZoneSearch(e.target.value);
            }}
            onFocus={() => setIsZoneDropdownOpen(true)}
            onBlur={() => setTimeout(() => setIsZoneDropdownOpen(false), 200)}
            placeholder="Kerko zonen..."
            className={`w-full text-sm py-3 px-3 outline-none focus:ring-primary ring-1 ring-blue-stroke
              transition-all duration-300 hover:ring-gray-500 rounded-lg ${
              selectedZone ? "pl-8" : ""
            }`}
          />
          {selectedZone && (
            <div className="absolute left-2 top-1/2 transform -translate-y-1/2 flex items-center">
              <img
                src={selectedZone.imageUrl}
                alt={selectedZone.name}
                className="w-4 h-4 object-contain"
              />
            </div>
          )}
          {selectedZone && (
            <button
              type="button"
              onClick={handleZoneClear}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
          {isZoneDropdownOpen && !selectedZone && (
            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {loadingZones ? (
                <div className="p-3 text-sm text-gray-500">
                  Duke ngarkuar...
                </div>
              ) : locationZones.length > 0 ? (
                locationZones.map((zone) => (
                  <div
                    key={zone.slug}
                    onClick={() => handleZoneSelect(zone)}
                    className="p-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                  >
                    <img
                      src={zone.imageUrl}
                      alt={zone.name}
                      className="w-4 h-4 object-contain"
                    />
                    <span className="text-sm">{zone.name}</span>
                  </div>
                ))
              ) : (
                <div className="p-3 text-sm text-gray-500">
                  Nuk u gjeten zona.
                </div>
              )}
            </div>
          )}
        </div>
      </InputWrapper>

      {/* Package selector */}
      {selectedZone && (
        <div className="mt-4">
          <InputWrapper label="Paketa eSIM" error={error}>
            {loadingPackages ? (
              <div className="p-3 text-sm text-gray-500 ring-1 ring-blue-stroke rounded-lg">
                Duke ngarkuar paketat...
              </div>
            ) : packages.length > 0 ? (
              <div className="grid gap-2 max-h-64 overflow-y-auto">
                {packages.map((pkg) => {
                  const isSelected = selectedPackage?.id === pkg.id;
                  return (
                    <div
                      key={pkg.id}
                      onClick={() => handlePackageSelect(pkg)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "border-primary bg-blue-50 ring-2 ring-primary"
                          : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {pkg.imageUrl && (
                          <img
                            src={pkg.imageUrl}
                            alt={pkg.name}
                            className="w-8 h-8 rounded object-contain"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {pkg.name}
                          </div>
                          <div className="text-xs text-gray-500 flex gap-3 mt-0.5">
                            <span>
                              {formatGB(pkg.totalGB, pkg.isUnlimited)}
                            </span>
                            <span>{pkg.validityDays} dite</span>
                            <span className="font-medium text-gray-700">
                              {pkg.cost} EUR
                            </span>
                          </div>
                        </div>
                        {isSelected && (
                          <span className="text-primary text-sm font-medium">
                            ✓
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-3 text-sm text-gray-500 ring-1 ring-blue-stroke rounded-lg">
                Nuk ka paketa per kete zone.
              </div>
            )}
          </InputWrapper>
        </div>
      )}

      {/* Selected package summary */}
      {selectedPackage && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs text-green-700">
            Paketa e zgjedhur: <strong>{selectedPackage.name}</strong> —{" "}
            {formatGB(selectedPackage.totalGB, selectedPackage.isUnlimited)},{" "}
            {selectedPackage.validityDays} dite, {selectedPackage.cost} EUR
          </p>
        </div>
      )}
    </div>
  );
};

export default EsimPackageSelector;
